import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import {
  CucsAdvisingApp,
  AdvisorAdmin,
  AdminLogin,
  ResetPassword,
  ProtectedRoute,
  AuthProvider,
  ADMIN_AUTH_ROUTES,
  ADMIN_ROUTES,
} from '@metlas/cucs-advising';
import { MarketplaceChat } from '@metlas/cu-marketplace';
import LandingPageV2 from '../../apps/metlas-landing/src/LandingPageV2';
import CareersPage from '../../apps/metlas-landing/src/CareersPage';
import ContactPage from '../../apps/metlas-landing/src/ContactPage';
import PropertyManagement from '@metlas/property-management';
import SampleCompetitorReport from '../../apps/property-management/src/SampleCompetitorReport';
import LeadAcquisition from '../../apps/property-management/src/LeadAcquisition';
import CompetitorAnalysis from '../../apps/property-management/src/CompetitorAnalysis';
import PropertyManagementApp from '../../apps/property-management/src/PropertyManagementApp';
import REInvestor from '@metlas/re-investor';
import LPProspecting from '../../apps/re-investor/src/LPProspecting';
import Underwriting from '../../apps/re-investor/src/Underwriting';
import Realtor from '@metlas/realtor';
import GmailExtractor from '@metlas/gmail-extractor';
import AuthCallback from './pages/AuthCallback';
import { PUBLIC_ROUTES } from './constants/routes';
import PrivacyPolicyTerms from './pages/PrivacyPolicyTerms';
import LandingPageRedirect from './pages/LandingPageRedirect';
import CaseStudyAccounting from './pages/case-studies/accounting';
import ChatBot from '@metlas/chat-bot';
import BuildingIntelligence from '@metlas/building-intelligence';
import BuildingIntelligenceExpediter from '../../apps/building-intelligence/src/BuildingIntelligenceExpediter';
import { FEATURE_FLAGS } from './constants/config';
import './styles/index.css';

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

createRoot(rootElement).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path={PUBLIC_ROUTES.HOME} element={<LandingPageV2 />} />
          <Route path={PUBLIC_ROUTES.LANDING_PAGE} element={<LandingPageRedirect />} />
          <Route path={PUBLIC_ROUTES.CAREERS} element={<CareersPage />} />
          <Route path={PUBLIC_ROUTES.CONTACT} element={<ContactPage />} />
          <Route path={PUBLIC_ROUTES.PROPERTY_MANAGEMENT} element={<PropertyManagement />} />
          <Route
            path={PUBLIC_ROUTES.PROPERTY_MANAGEMENT_SAMPLE_REPORT}
            element={<SampleCompetitorReport />}
          />
          <Route path={PUBLIC_ROUTES.PROPERTY_MANAGEMENT_APP} element={<PropertyManagementApp />} />
          <Route
            path={PUBLIC_ROUTES.PROPERTY_MANAGEMENT_LEAD_ACQUISITION}
            element={<LeadAcquisition />}
          />
          <Route
            path={PUBLIC_ROUTES.PROPERTY_MANAGEMENT_COMPETITOR_ANALYSIS}
            element={<CompetitorAnalysis />}
          />
          <Route path={PUBLIC_ROUTES.RE_INVESTOR} element={<REInvestor />} />
          <Route path={PUBLIC_ROUTES.RE_INVESTOR_LP_PROSPECTING} element={<LPProspecting />} />
          <Route path={PUBLIC_ROUTES.RE_INVESTOR_UNDERWRITING} element={<Underwriting />} />
          <Route path={PUBLIC_ROUTES.REALTOR} element={<Realtor />} />
          <Route path={PUBLIC_ROUTES.GMAIL_EXTRACTOR} element={<GmailExtractor />} />
          <Route path={PUBLIC_ROUTES.GMAIL_EXTRACTOR_CALLBACK} element={<AuthCallback />} />
          <Route path={PUBLIC_ROUTES.PRIVACY_TERMS} element={<PrivacyPolicyTerms />} />
          <Route path={PUBLIC_ROUTES.CASE_STUDY_ACCOUNTING} element={<CaseStudyAccounting />} />
          <Route path={PUBLIC_ROUTES.TRUSTWORTHY_AGENT} element={<ChatBot />} />
          <Route path={PUBLIC_ROUTES.BUILDING_INTELLIGENCE} element={<BuildingIntelligence />} />
          <Route
            path={PUBLIC_ROUTES.EXPEDITER_BUILDING_INTELLIGENCE}
            element={<BuildingIntelligenceExpediter />}
          />

          {/* CU Marketplace Bot - Controlled by FEATURE_FLAGS.ENABLE_MARKETPLACE */}
          {FEATURE_FLAGS.ENABLE_MARKETPLACE && (
            <Route path={PUBLIC_ROUTES.CU_MARKETPLACE} element={<MarketplaceChat />} />
          )}

          {/* Auth routes */}
          <Route path={ADMIN_AUTH_ROUTES.LOGIN} element={<AdminLogin />} />
          <Route path={ADMIN_AUTH_ROUTES.RESET_PASSWORD} element={<ResetPassword />} />

          {/* Public chat — no login required as of fea/no_login_chat.
              Anonymous visitors are identified by the `cucs_visitor_id`
              HTTP-only cookie minted server-side; logged-in admins still
              get their JWT-derived identity for analytics + the Metlas
              toggle. Per-visitor and per-IP rate limits are enforced
              server-side; see apps/cucs-advising/RATE_LIMITS.md. The
              admin dashboard routes below remain login-gated. */}
          <Route path={PUBLIC_ROUTES.CUCS_ADVISING} element={<CucsAdvisingApp />} />

          {/* Admin routes - redirect base to overview */}
          <Route
            path={ADMIN_ROUTES.BASE}
            element={<Navigate to={ADMIN_ROUTES.OVERVIEW} replace />}
          />

          {/* Protected admin routes */}
          <Route
            path={ADMIN_ROUTES.OVERVIEW}
            element={
              <ProtectedRoute>
                <AdvisorAdmin view="overview" />
              </ProtectedRoute>
            }
          />
          <Route
            path={ADMIN_ROUTES.SUPPORT}
            element={
              <ProtectedRoute>
                <AdvisorAdmin view="student-support" />
              </ProtectedRoute>
            }
          />
          <Route
            path={ADMIN_ROUTES.QA_MANAGEMENT}
            element={
              <ProtectedRoute>
                <AdvisorAdmin view="qa-management" />
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>
);
